import { useState } from 'react'
import { api, errorMessage } from '../api/client'
import type { Supply } from '../api/types'
import { DayAverageByItem } from '../components/DayAverageByItem'
import {
  Amt,
  Card,
  Dt,
  Empty,
  Field,
  LONG_LIST_CAP,
  LongListNote,
  Note,
  NumberCell,
  PageHead,
  Wt,
} from '../components/ui'
import { useApi } from '../hooks/useApi'
import { today } from '../lib/format'

/** A week back, because a rate is often agreed a few days after the delivery. */
function weekAgo(): string {
  const d = new Date()
  d.setDate(d.getDate() - 7)
  return d.toISOString().slice(0, 10)
}

/**
 * Setting what each delivery is worth.
 *
 * <p>This is the evening's job, and it belongs to the shopkeeper, not the app.
 * The day's average per flower is on the screen to read - a grower who sent
 * motia can be paid against what motia did - but the rate itself is typed by
 * hand, because it takes in things the book never sees: whose flower was fresher,
 * what was promised last week, who is owed a favour.
 *
 * <p>This page used to track each delivery being sold off piece by piece, and
 * offered one button: pay the grower whatever his own lot averaged. That stopped
 * making sense when selling moved to the flower rather than the sack - nothing
 * is attached to a delivery any more, so every lot read zero sold and zero
 * average, which is worse than useless because it looks like an answer.
 */
export function SupplyRates() {
  const [from, setFrom] = useState(weekAgo())
  const [to, setTo] = useState(today())
  const [onlyUnpriced, setOnlyUnpriced] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const supplies = useApi<Supply[]>(`/supplies/daybook?from=${from}&to=${to}`, [from, to])
  const all = supplies.data ?? []
  const rows = onlyUnpriced ? all.filter((s) => s.awaitingRate) : all

  /*
   * Capped like the other long lists, though this one should never reach it.
   *
   * The page opens on a week with the unpriced filter already on, and a week
   * where two hundred deliveries are still waiting on a price is not a busy
   * shop, it is a shop that has stopped pricing. The count above the table is
   * taken over every row either way, so the number waiting stays true even if
   * the list below it stops.
   */
  const shown = rows.length > LONG_LIST_CAP ? rows.slice(0, LONG_LIST_CAP) : rows

  async function setRate(supply: Supply, rate: string) {
    setError(null)
    setDone(null)
    try {
      await api.put(`/supplies/${supply.id}/rate`, { rate })
      setDone(`${supply.vendorName} — ریٹ ${rate} لگ گیا۔`)
      supplies.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  async function setKiraya(supply: Supply, kiraya: string) {
    setError(null)
    setDone(null)
    try {
      await api.put(`/supplies/${supply.id}/kiraya`, { kiraya })
      setDone(`${supply.vendorName} — کرایہ ${kiraya} ہو گیا۔`)
      supplies.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const waiting = all.filter((s) => s.awaitingRate).length

  return (
    <>
      <PageHead
        title="ریٹ لگائیں"
        hint="زمیندار کے مال کا ریٹ اور کرایہ۔ نیچے اُس دن کا اوسط دیکھ کر لگائیں۔"
      />

      <DayAverageByItem />

      {error && <Note kind="error">{error}</Note>}
      {done && <Note kind="info">{done}</Note>}

      <Card
        title={`سپلائی${waiting > 0 ? ` — ${waiting} کا ریٹ باقی ہے` : ''}`}
        actions={
          <label style={{ fontSize: 14, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={onlyUnpriced}
              onChange={(e) => setOnlyUnpriced(e.target.checked)}
            />
            صرف وہ جن کا ریٹ باقی ہے
          </label>
        }
      >
        <div className="row">
          <Field label="اس تاریخ سے">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="اس تاریخ تک">
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>تاریخ</th>
                <th>زمیندار</th>
                <th>وزن</th>
                <th>ریٹ</th>
                <th>کرایہ</th>
                <th>مال کی رقم</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((supply) => (
                <tr key={supply.id}>
                  <td>
                    <Dt value={supply.date} />
                  </td>
                  <td className="name-cell">
                    {supply.vendorName}{' '}
                    {supply.awaitingRate && <span className="pill pill--wait">ریٹ باقی</span>}
                  </td>
                  <td className="num">
                    <Wt value={supply.weight} />
                  </td>
                  <td className="num">
                    <NumberCell
                      ariaLabel="ریٹ"
                      value={supply.rate ?? ''}
                      width={100}
                      onCommit={(value) => void setRate(supply, value)}
                    />
                  </td>
                  <td className="num">
                    <NumberCell
                      ariaLabel="کرایہ"
                      value={supply.kiraya ?? '0'}
                      width={90}
                      onCommit={(value) => void setKiraya(supply, value)}
                    />
                  </td>
                  <td className="num">
                    <strong>{supply.amount ? <Amt value={supply.amount} /> : '—'}</strong>
                  </td>
                </tr>
              ))}

              {rows.length === 0 && !supplies.loading && (
                <tr>
                  <td colSpan={6}>
                    <Empty>
                      {onlyUnpriced
                        ? 'اِن دنوں کی ہر سپلائی کا ریٹ لگ چکا ہے۔'
                        : 'اِن دنوں میں کوئی سپلائی نہیں آئی۔'}
                    </Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <LongListNote shown={shown.length} total={rows.length} />

        <p className="muted small" style={{ marginTop: 10 }}>
          ریٹ یا کرایہ کے خانے میں لکھ کر <strong>Enter</strong> دبائیں یا باہر ٹچ کریں۔ مال کی رقم
          خود بن جائے گی (وزن × ریٹ)، اور زمیندار کا پورا حساب «زمیندار کھاتہ» میں بنے گا۔
        </p>
      </Card>
    </>
  )
}
